import firebase from '@/plugins/firebase';
import { readSessionStorageCache, writeSessionStorageCache } from '@/utils/sessionStorageCache';

const CACHE_VERSION = 1;
const CACHE_TTL_MS = 60 * 1000;
const CACHE_KEY_PREFIX = 'dashboard-selector';
const CACHE_WARNING = '[dashboardAccess] Selector cache unavailable; querying Firestore.';

function getCacheKey(universeId, email) {
  let projectId;
  try {
    projectId = firebase.app().options.projectId;
  } catch (error) {
    projectId = null;
  }

  const projectScope = projectId || process.env.VUE_APP_STUDIO_FIREBASE_CONFIG_PROJECT_ID || process.env.NODE_ENV || 'default';
  return `${CACHE_KEY_PREFIX}:v${CACHE_VERSION}:${encodeURIComponent(projectScope)}:${encodeURIComponent(String(universeId))}:${encodeURIComponent(String(email))}`;
}

function hasExactKeys(value, keys) {
  return value &&
     typeof value === 'object' &&
     !Array.isArray(value) &&
     Object.keys(value).length === keys.length &&
     keys.every(key => Object.prototype.hasOwnProperty.call(value, key));
}

function isValidDashboard(dashboard) {
  return hasExactKeys(dashboard, ['id', 'data']) &&
     typeof dashboard.id === 'string' &&
     hasExactKeys(dashboard.data, ['title']) &&
     typeof dashboard.data.title === 'string';
}

function isValidDashboards(dashboards) {
  return Array.isArray(dashboards) && dashboards.every(isValidDashboard);
}

export async function getDashboardsForUserInUniverse(universeId, email) {
  const cacheKey = getCacheKey(universeId, email);
  const cachedDashboards = readSessionStorageCache({
    cacheKey,
    version: CACHE_VERSION,
    isValidData: isValidDashboards,
    warningMessage: CACHE_WARNING,
  });
  if (cachedDashboards) return cachedDashboards;

  const snapshot = await firebase.firestore()
    .collection('universes')
    .doc(String(universeId))
    .collection('dashboards')
    .where('userEmails', 'array-contains', email)
    .get();

  const dashboards = snapshot.docs.map(document => {
    const title = document.data().title;
    return {
      id: document.id,
      data: { title: typeof title === 'string' ? title : '' },
    };
  });

  writeSessionStorageCache({
    cacheKey,
    version: CACHE_VERSION,
    ttlMs: CACHE_TTL_MS,
    data: dashboards,
    warningMessage: CACHE_WARNING,
  });
  return dashboards;
}
